import type { ClientPositionView } from "@solomon-apps/shared";
import type { BigDecimal } from "@solomon-labs/bigdecimal";

import { groupHoldings, sumHoldingBalances } from "@/pages/Positions/holdings";

import { toUsdv } from "./positionAmounts";
import { usePositionBalances } from "./usePositionBalances";

export { toUsdv };

/** Σ lifetime rewards (`totalRewards`) across positions. */
export function sumUsdvEarned(positions: ClientPositionView[]): BigDecimal {
    return positions.reduce((sum, p) => sum.add(toUsdv(p.totalRewards)), toUsdv("0"));
}

export interface UsePortfolioTotalsResult {
    /**
     * Σ USDv held across the given positions' holdings, each holding counted
     * once (several Programs on one wallet are one holding) — the live
     * on-chain balance for Address-style Program Surfaces, observed LP
     * principal and manual amounts for other surfaces. Null when any holding
     * is pending or unavailable.
     */
    totalUsdvPositions: BigDecimal | null;
    isLoadingPositions: boolean;
    /** Σ lifetime rewards (`totalRewards`) across the given positions. */
    totalUsdvEarned: BigDecimal;
}

/**
 * Portfolio-wide USDv totals for the key-stats row. Groups positions into
 * holdings (`groupHoldings`) so a wallet under several Programs adds its funds
 * once, sharing `usePositionBalances` query keys so each address's balance is
 * fetched once per cache window regardless of how many rows also display it.
 */
export function usePortfolioTotals(positions: ClientPositionView[]): UsePortfolioTotalsResult {
    const balances = usePositionBalances(positions);
    const resolved = [...balances.values()];
    return {
        totalUsdvPositions: sumHoldingBalances(groupHoldings(positions, { balances })),
        isLoadingPositions: resolved.some((balance) => balance.isLoading),
        totalUsdvEarned: sumUsdvEarned(positions)
    };
}
